import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/format";
import { Loader2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RupiahInput } from "@/components/RupiahInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const SELECT_CLS = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm";
export const PAY_STATUS_LABEL = { cash: "Cash", transfer: "Transfer", split: "Cash + Transfer", kredit: "Kredit" };
export const today = () => new Date().toLocaleDateString("en-CA");

const STATE_META = {
  lunas: { label: "Lunas", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  sebagian: { label: "Sebagian", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  belum_lunas: { label: "Belum Lunas", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

export function PayStateBadge({ state, testId }) {
  const m = STATE_META[state] || STATE_META.belum_lunas;
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${m.cls}`} data-testid={testId}>{m.label}</span>;
}

export function useCompanies() {
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  return companies;
}

export function AccountList({ accounts, testId }) {
  if (!accounts?.length) return null;
  return (
    <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs space-y-0.5" data-testid={testId}>
      <p className="text-muted-foreground">Rekening PT tujuan:</p>
      {accounts.map((a, i) => (
        <p key={i} className="font-mono text-brand"><span className="font-semibold">{a.bank_name || "Bank"}</span> · {a.account_number}{a.account_holder ? ` · a.n. ${a.account_holder}` : ""}</p>
      ))}
    </div>
  );
}

export const accLabel = (a) => (a ? `${a.bank_name || "Bank"} · ${a.account_number}${a.account_holder ? ` · a.n. ${a.account_holder}` : ""}` : "");

export function CompanyPicker({ companies, value, onChange, account, onAccountChange, testId, noAccount }) {
  const c = companies.find((x) => x.id === value);
  const accs = noAccount ? [] : (c?.accounts || []);
  useEffect(() => {
    if (noAccount) return;
    if (accs.length === 1 && account !== accs[0].account_number) onAccountChange(accs[0].account_number);
    else if (accs.length !== 1 && account && !accs.some((a) => a.account_number === account)) onAccountChange("");
  }, [value, accs, account, onAccountChange, noAccount]);
  return (
    <div>
      <Label>PT Tujuan <span className="text-red-500">*</span></Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${SELECT_CLS} mt-1.5`} data-testid={testId}>
        <option value="">{companies.length ? "Pilih PT tujuan" : "Belum ada PT di Master PT"}</option>
        {companies.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      {accs.length > 0 && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 space-y-1" data-testid={`${testId}-accounts`}>
          <p className="text-xs text-muted-foreground px-1">Pilih rekening tujuan <span className="text-red-500">*</span></p>
          {accs.map((a, i) => {
            const on = account === a.account_number;
            return (
              <label key={i} className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs cursor-pointer border ${on ? "bg-white border-brand" : "border-transparent hover:bg-white/70"}`} data-testid={`${testId}-account-${i}`}>
                <input type="radio" name={`${testId}-acc`} checked={on} onChange={() => onAccountChange(a.account_number)} className="accent-emerald-700 h-4 w-4" />
                <span className="font-mono text-brand"><span className="font-semibold">{a.bank_name || "Bank"}</span> · {a.account_number}{a.account_holder ? ` · a.n. ${a.account_holder}` : ""}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PaymentDialog({ tx, companies, onDone, testId }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const blank = { date: today(), amount: "", method: "cash", company_id: user?.company_id || "", account_number: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setAcc = useCallback((v) => setF((p) => ({ ...p, account_number: v })), []);
  const amt = Number(f.amount) || 0;
  const save = async (e) => {
    e.preventDefault();
    if (!f.company_id) { toast.error("Pilih PT tujuan"); return; }
    if (f.method === "transfer" && !f.account_number) { toast.error("Pilih PT dan nomor rekening tujuan"); return; }
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/payments`, { ...f, amount: amt });
      toast.success("Pembayaran dicatat.");
      setOpen(false); setF(blank); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-emerald-800" disabled={tx.remaining <= 0} data-testid={testId}>
          <Wallet className="h-3.5 w-3.5 mr-1.5" /> Catat Pembayaran
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Catat Pembayaran — {tx.nota_number}</DialogTitle>
          <DialogDescription>{tx.customer_name} · Sisa tagihan <span className="font-mono font-semibold">{formatRupiah(tx.remaining)}</span></DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal Bayar</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} required className="mt-1.5" data-testid="payment-date" /></div>
            <div><Label>Jumlah Bayar</Label><RupiahInput value={f.amount} onChange={(e) => set("amount", e.target.value)} required placeholder="0" className="mt-1.5 font-mono" data-testid="payment-amount" /></div>
          </div>
          <div>
            <Label>Metode Bayar</Label>
            <select value={f.method} onChange={(e) => set("method", e.target.value)} className={`${SELECT_CLS} mt-1.5`} data-testid="payment-method">
              <option value="cash">Cash</option><option value="transfer">Transfer</option>
            </select>
          </div>
          <CompanyPicker companies={companies} value={f.company_id} onChange={(v) => set("company_id", v)} account={f.account_number} onAccountChange={setAcc} noAccount={f.method !== "transfer"} testId="payment-company" />
          <div className="rounded-md bg-secondary px-3 py-2 text-xs flex justify-between" data-testid="payment-remaining-preview">
            <span>Sisa setelah bayar</span>
            <span className={`font-mono font-semibold ${tx.remaining - amt < 0 ? "text-rose-600" : "text-brand"}`}>{formatRupiah(tx.remaining - amt)}</span>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="payment-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Pembayaran"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
